import React, { useMemo, useRef, useState } from 'react';
import { Layout } from 'tea-component';
import { connect, useLocation } from 'umi';
import ContentBody from '@op/components/ContentBody';
import { isDune, PAGE_NAME } from '@op/constants/page';
import BubbleCommon, {
  summaryTopicTrackingTipsText,
} from '@op/components/BubbleCommon';
import { useInitBuriedPointInfo } from '@op/operationLog/buriedPoint';
import topicTrackingOperation from '@op/operationLog/pages/topicTracking';
import { FilterFixed, useFilterDataHook } from '@op/components/FilterCommon';
import CardLoading from '@op/components/CardLoading';
import {
  SentimentType,
  FilterContext,
  TopicTypeType,
  TopicLevelType,
} from '../context';
import TopSelectFilter from './TopSelectFilter';
import useTranslateTopic from '@op/hooks/useTranslateTopic';
import useDealwithTopic from '../hooks/useDealwithTopic';
import { clickTopicToFeeds } from '../utils';
import { useDrilldownParams } from '@op/hooks/useDrilldownParams';
import TopicChartInfo from './TopicChartInfo';
import TopicPerformance from './TopicPerformance';
import useLanguage from '@op/hooks/useLanguage';
export type TabType = 'Overview' | 'Tree' | 'Summary' | 'excel';
const { Content } = Layout;

function TopicTracking({ gameInfo }: any) {
  const scrollBody = useRef(null);

  useInitBuriedPointInfo({ buriedPoint: topicTrackingOperation });

  const { formatMessage } = useLanguage();
  const location: any = useLocation();

  const [topicType, setTopicType] = useState<TopicTypeType>('all_topic');
  const [topicLevel, setTopicLevel] = useState<TopicLevelType>(
    location?.state?.topicLevel || location?.query?.topicLevel || 'L1',
  );
  const [sentiment, setSentiment] = useState<SentimentType>(
    location?.state?.sentimentType || location?.query?.sentimentType || 'total',
  );

  const [userTopicAccount, setUserTopicAccount] = useState<{
    topic: Array<any>;
    topicType: TopicTypeType;
  }>({
    topic: [],
    topicType: 'all_topic',
  });

  const [topics, setTopics] = useState<{
    parent: Array<string>;
    children: Array<string>;
    flag: boolean;
  }>({
    parent: [],
    children: [],
    flag: true,
  });

  const [isTopicSentiment, setIsTopicSentiment] = useState<string>('sentiment');

  const { loading, filter, changeFilter, filterOptions, filterCommonParams } =
    useFilterDataHook({
      pageName: 'Summary_TopicTracking',
      gameInfo,
    });
  const { setTranslateTopicMap } = useTranslateTopic();
  const { goPage } = useDrilldownParams();
  const [condTopicInfo, setCondTopicInfo] = useState([]);
  const keyAndAllTopic = useRef<{
    all_topic: Array<string>;
    key_topic: Array<string>;
  }>({
    all_topic: [],
    key_topic: [],
  });
  const { topicOneLevel, topicTwoLevel, topicTree, topicOptions, transTopic } =
    useDealwithTopic({ condTopicList: condTopicInfo });

  const newFilter = useMemo(() => {
    return {
      ...filter,
      sentiment_type: sentiment,
      topic_level: topicLevel,
      ...userTopicAccount,
      ...(isTopicSentiment === 'sub_topic'
        ? { topic_sentiment_type: isTopicSentiment }
        : {}),
    };
  }, [filter, sentiment, topicLevel, userTopicAccount, isTopicSentiment]);

  function changeTopicType(value: TopicTypeType) {
    setUserTopicAccount({
      topic: keyAndAllTopic.current[value],
      topicType: value,
    });
    setTopicType(value);
  }

  const filterContextValue = useMemo(() => {
    const clickTopicToFeedsFunc = (
      topic: string,
      topicLevel: TopicLevelType,
    ) => {
      let topics: Array<any> = [transTopic(topic, topicLevel, 'en-US', true)];
      if (topicLevel === 'L1') {
        topics = (topicTree[topic] || [])?.map((item: any) =>
          transTopic(item, 'L2', 'en-US', true),
        );
      }
      clickTopicToFeeds(topics, filter, goPage);
    };

    return {
      topics,
      sentimentType: sentiment,
      filter: newFilter,
      gameInfo,
      topicOneLevel,
      topicTwoLevel,
      topicTree,
      transTopic,
      clickTopicToFeedsFunc,
      topicLevel,
      topicOptions,
      topicList: {
        topic_trans: condTopicInfo,
        key_topic: undefined,
      },
    };
  }, [
    topicOneLevel,
    topicTwoLevel,
    topics,
    newFilter,
    gameInfo,
    topicTree,
    transTopic,
    topicOptions,
    goPage,
  ]);

  const pageName = isDune({
    gameInfo,
    replace: 'Topic Analysis',
    current: PAGE_NAME.TOPIC_TRACKING,
  });
  return (
    <Content className="dbo-summary" ref={scrollBody}>
      <ContentBody
        contentHeard={
          <Content.Header
            title={formatMessage({ id: pageName })}
            subtitle={
              <span className="dbo-header-subtitle ">
                {formatMessage({
                  id: 'The most discussed topic content based on the reviews labeled by topic classification',
                })}
                <BubbleCommon tipsMap={summaryTopicTrackingTipsText} />
              </span>
            }
          ></Content.Header>
        }
        pageName={pageName}
      >
        <FilterFixed
          filter={filterOptions}
          scrollBody={scrollBody}
          userSynchronousValue={userTopicAccount}
          {...filterCommonParams}
          finallyDealwithDefaultValue={(data, otherParams, isDefaultValue) => {
            if (data.topic.length > 0 && !isDefaultValue) {
              setUserTopicAccount({
                ...userTopicAccount,
                topicType: 'all_topic',
              });
              setTopicType('all_topic');
            }
            return data;
          }}
          defaultConfig={{
            topic: {
              hasAllKeyTopicSelectOptions: true,
            },
          }}
          backCondOriginData={(value: any) => {
            const keyTopic: Array<string> = Object.values(
              value.key_topic || {},
            ).flat(3) as Array<string>;

            const allTopic = value.topic_trans.map((item: any) =>
              item.topic_to_en.toLocaleLowerCase(),
            );
            keyAndAllTopic.current = {
              all_topic: [...allTopic, 'other'],
              key_topic: keyTopic.length === 0 ? ['-99999'] : keyTopic,
            };

            userTopicAccount.topic = keyTopic;
            setUserTopicAccount(userTopicAccount);
            setCondTopicInfo(value?.topic_trans || []);
            setTranslateTopicMap(value?.topic_trans || []);
          }}
          confirm={(value?: any) => {
            const { topic_one_level, topic, topicType, ...otherFilterInfo } =
              value || {};
            setTopics({
              parent: topic_one_level,
              children: topic,
              flag: topic_one_level?.length === 0,
            });
            setTopicType(topicType || 'all_topic');
            const localTopic =
              topic.length === 0
                ? keyAndAllTopic.current[userTopicAccount.topicType]
                : topic;
            userTopicAccount.topic = localTopic;
            setUserTopicAccount(userTopicAccount);
            changeFilter({
              ...otherFilterInfo,
              topic: localTopic,
              ...gameInfo,
            });
          }}
          finallyDealwithResetValue={() => {
            userTopicAccount.topic = [];
            setUserTopicAccount(userTopicAccount);
          }}
        ></FilterFixed>

        <CardLoading loading={loading}>
          <TopSelectFilter
            sentiment={sentiment}
            setSentiment={setSentiment}
            topicType={topicType}
            setTopicType={changeTopicType}
            topicLevel={topicLevel}
            setTopicLevel={setTopicLevel}
            isTopicSentiment={isTopicSentiment}
            setIsTopicSentiment={setIsTopicSentiment}
          ></TopSelectFilter>

          <FilterContext.Provider value={filterContextValue}>
            <div className="dbo-overview-table-wrapper">
              <TopicChartInfo topicType={topicType} />
              <TopicPerformance
                isTopicSentiment={isTopicSentiment}
              ></TopicPerformance>
            </div>
          </FilterContext.Provider>
        </CardLoading>
      </ContentBody>
    </Content>
  );
}

export default connect((state: { opinionCommon: any }) => {
  const { opinionCommon } = state;

  return {
    gameInfo: opinionCommon.gameInfo,
  };
})(TopicTracking);
